import type { MessengerComposerDraft, MessengerDraftTargetKind } from '@/api/client';

const draftByKey = new Map<string, MessengerComposerDraft>();
const pendingLocalKeys = new Set<string>();

const listListeners = new Set<() => void>();
const listenersByKey = new Map<string, Set<() => void>>();

export function draftStoreKey(kind: MessengerDraftTargetKind, id: string): string {
  return `${kind}:${id}`;
}

function emitSet(set: Set<() => void> | undefined) {
  if (!set) return;
  for (const cb of set) cb();
}

function emitKey(key: string) {
  emitSet(listenersByKey.get(key));
}

function emitList() {
  for (const cb of listListeners) cb();
}

function areDraftsEqual(a: MessengerComposerDraft, b: MessengerComposerDraft): boolean {
  return (
    a.target_kind === b.target_kind &&
    a.target_id === b.target_id &&
    a.text === b.text &&
    a.reply_to_message_id === b.reply_to_message_id &&
    a.forward_message_ids.length === b.forward_message_ids.length &&
    a.forward_message_ids.every((id, i) => id === b.forward_message_ids[i]) &&
    a.updated_at === b.updated_at
  );
}

function isIncomingStale(existing: MessengerComposerDraft, incoming: MessengerComposerDraft): boolean {
  return existing.updated_at > incoming.updated_at;
}

function clearPendingIfServerConfirmed(
  key: string,
  serverUpdatedAt: string,
  localUpdatedAt?: string
): void {
  if (!pendingLocalKeys.has(key)) return;
  if (localUpdatedAt === undefined || serverUpdatedAt >= localUpdatedAt) {
    pendingLocalKeys.delete(key);
  }
}

export type UpsertMessengerDraftOptions = {
  /** Optimistic local write before PUT lands; protected from stale snapshot eviction. */
  source?: 'local' | 'server';
};

export function setMessengerDraftsSnapshot(drafts: MessengerComposerDraft[]): void {
  const prevByKey = new Map(draftByKey);
  const nextByKey = new Map<string, MessengerComposerDraft>();
  const changedKeys = new Set<string>();
  const nextKeys = new Set<string>();

  for (const incoming of drafts) {
    const key = draftStoreKey(incoming.target_kind, incoming.target_id);
    nextKeys.add(key);
    const prev = prevByKey.get(key);
    const merged =
      prev && (isIncomingStale(prev, incoming) || areDraftsEqual(prev, incoming)) ? prev : incoming;
    if (merged !== prev) changedKeys.add(key);
    nextByKey.set(key, merged);
    if (!prev || !isIncomingStale(prev, incoming)) {
      clearPendingIfServerConfirmed(key, incoming.updated_at, prev?.updated_at);
    }
  }

  for (const key of prevByKey.keys()) {
    if (nextKeys.has(key) || !pendingLocalKeys.has(key)) continue;
    const prev = prevByKey.get(key)!;
    nextByKey.set(key, prev);
    nextKeys.add(key);
  }

  const prevKeys = new Set(prevByKey.keys());
  let listStructureChanged = prevKeys.size !== nextKeys.size;

  for (const key of prevKeys) {
    if (!nextKeys.has(key)) {
      changedKeys.add(key);
      listStructureChanged = true;
    }
  }

  if (!listStructureChanged) {
    for (const key of nextKeys) {
      if (!prevKeys.has(key)) {
        listStructureChanged = true;
        break;
      }
    }
  }

  draftByKey.clear();
  for (const [key, draft] of nextByKey) {
    draftByKey.set(key, draft);
  }

  for (const key of changedKeys) emitKey(key);
  if (listStructureChanged) emitList();
}

export function upsertMessengerDraftInStore(
  draft: MessengerComposerDraft,
  options?: UpsertMessengerDraftOptions
): void {
  const key = draftStoreKey(draft.target_kind, draft.target_id);
  const existing = draftByKey.get(key);
  const source = options?.source ?? 'server';

  if (source === 'local') {
    pendingLocalKeys.add(key);
  } else {
    clearPendingIfServerConfirmed(key, draft.updated_at, existing?.updated_at);
  }

  if (existing && isIncomingStale(existing, draft)) return;

  const merged = existing && areDraftsEqual(existing, draft) ? existing : draft;
  if (merged === existing) return;

  const wasNew = !existing;
  draftByKey.set(key, merged);
  emitKey(key);
  if (wasNew) emitList();
}

export function removeMessengerDraftFromStore(kind: MessengerDraftTargetKind, id: string): void {
  const key = draftStoreKey(kind, id);
  if (!draftByKey.has(key)) return;

  pendingLocalKeys.delete(key);
  draftByKey.delete(key);
  emitKey(key);
  emitList();
}

export function removeMessengerDraftFromStoreAtRevision(
  kind: MessengerDraftTargetKind,
  id: string,
  updatedAt: string
): void {
  const existing = getMessengerDraft(kind, id);
  if (existing && existing.updated_at > updatedAt) return;
  removeMessengerDraftFromStore(kind, id);
}

export function getMessengerDraft(
  kind: MessengerDraftTargetKind,
  id: string
): MessengerComposerDraft | undefined {
  return draftByKey.get(draftStoreKey(kind, id));
}

export function subscribeMessengerDraft(
  kind: MessengerDraftTargetKind,
  id: string,
  onChange: () => void
): () => void {
  const key = draftStoreKey(kind, id);
  let set = listenersByKey.get(key);
  if (!set) {
    set = new Set();
    listenersByKey.set(key, set);
  }
  set.add(onChange);
  return () => {
    set!.delete(onChange);
    if (set!.size === 0) listenersByKey.delete(key);
  };
}

export function subscribeMessengerDraftsList(onChange: () => void): () => void {
  listListeners.add(onChange);
  return () => listListeners.delete(onChange);
}

export function resetMessengerDraftsStore(): void {
  draftByKey.clear();
  pendingLocalKeys.clear();
  listListeners.clear();
  listenersByKey.clear();
}

export function applyMessengerDraftSocketEvent(payload: {
  target_kind: MessengerDraftTargetKind;
  target_id: string;
  draft: MessengerComposerDraft | null;
  updated_at?: string;
}): void {
  if (payload.draft === null) {
    if (!payload.updated_at) return;
    removeMessengerDraftFromStoreAtRevision(
      payload.target_kind,
      payload.target_id,
      payload.updated_at
    );
    return;
  }

  upsertMessengerDraftInStore(payload.draft);
}
